import React from 'react';
import PropTypes from 'prop-types';
import { Loader } from 'semantic-ui-react';
import { Input, Button, Icon } from '@weezevent/nacre';
import SelectableImage from './components/selectableImage';
import { Trans, useTranslation } from 'react-i18next';
import useDebounceSearch from './useDebounceSearch';
import ExternalLink from './components/externalLink';
import './components.gallery.scss';
import ModalEditImage from '../upload/components/modalEditImage';
import UploadRequest from './request';
import { fetcher } from '@weezevent/weezjs-core';

const PublicImages = ({
    organization,
    onUpload = () => {},
    onClose = () => {},
    isBanner,
    apiKeyUnsplash,
    fileTypes = PublicImages.fileTypes,
    maxSize = 5242880,
    cropModalProps = {
        ratio: isBanner ? 16 / 9 : null,
        forceRatio: isBanner,
        minWidth: 180,
        maxWidth: Infinity,
        minHeight: 180,
        maxHeight: Infinity
    }
}) => {
    const [images, setImages] = React.useState([]);
    const [page, setPage] = React.useState(1);
    const [nextPage, setNextPage] = React.useState();
    const [loadingGallery, setLoadingGallery] = React.useState(true);
    const [imgToUplaod, setImgToUpload] = React.useState(null);
    const [isUploading, setIsUploading] = React.useState(false);

    const getUnsplashImages = React.useCallback(
        async (search, nextPage = false) => {
            const returnData = data => {
                if (search && nextPage) {
                    return images.concat(data.results);
                } else if (search && !nextPage) {
                    return data.results;
                } else if (!search && nextPage) {
                    return images.concat(data);
                } else {
                    return data;
                }
            };

            const response = await fetch(
                `https://api.unsplash.com/${
                    search ? `/search/photos/?orientation=landscape&page=${page}&per_page=10&query=${search}` : `/photos/?orientation=landscape&page=${page}`
                }`,
                {
                    method: 'GET',
                    headers: {
                        authorization: `Client-ID ${apiKeyUnsplash}`
                    }
                }
            );
            response
                .json()
                .then(data => {
                    if (response.headers.get('link')) {
                        setNextPage(true);
                    }
                    setImages(returnData(data));
                })
                .catch(e => {
                    console.log(e);
                })
                .finally(() => {
                    setLoadingGallery(false);
                });
        },
        [page, images, apiKeyUnsplash]
    );

    const sendMarkDownload = React.useCallback(
        url => {
            fetch(url, {
                headers: {
                    authorization: `Client-ID ${apiKeyUnsplash}`
                },
                cache: false
            }).catch(e => {
                console.log(e);
            });
        },
        [apiKeyUnsplash]
    );

    const [searchQuery, setSearchQuery] = useDebounceSearch(getUnsplashImages);

    React.useEffect(() => {
        getUnsplashImages(searchQuery, true);
    }, [page]);

    const { t } = useTranslation();

    // Convert unsplash url to file
    const downloadImage = React.useCallback(url => {
        return fetch(url)
            .then(response => response.blob())
            .then(blob => {
                const file = new File([blob], { type: 'image/jpeg' });
                return file;
            });
    }, []);

    // Upload unsplash image to EC2
    const handleImageUpload = React.useCallback(
        (cropImage = null) => {
            const image = cropImage || imgToUplaod;

            setIsUploading(true);
            const authorizationURL = `${window.weezConfig.api.apiUrl}/sites/organizations/${organization.id}/gallery`;
            const uploadRequest = new UploadRequest(authorizationURL, { fetch: fetcher });

            return uploadRequest
                .uploadFile(image)
                .then(({ key, href }) => {
                    onUpload(href);
                })
                .catch(e => console.error(e))
                .finally(() => {
                    setIsUploading(false);
                    onClose();
                });
        },
        [imgToUplaod, organization]
    );

    return (
        <>
            <div className="wz-search-unsplash">
                <p className="wz-powered-by">
                    <Trans i18nKey="ticket.event.minisite.powered-by">
                        <ExternalLink href="https://unsplash.com/?utm_source=weeztarget&utm_medium=referral">Unsplash</ExternalLink>
                    </Trans>
                </p>
                <Input
                    leftIcon={<Icon name="search" />}
                    value={searchQuery}
                    placeholder={t(['ticket.event.minisite.search'])}
                    onChange={({ target: { value } }) => setSearchQuery(value)}
                />
            </div>
            <div>
                <div className="wz-unsplash-container">
                    {loadingGallery && (
                        <div className="wz-unsplash-loading">
                            <Loader active />
                        </div>
                    )}
                    {images &&
                        images.map((img, key) => (
                            <SelectableImage
                                key={key}
                                thumb={img.urls.thumb}
                                url={img.urls.regular}
                                onSelected={url => {
                                    sendMarkDownload(img.links.download_location);
                                    downloadImage(url).then(file => {
                                        setImgToUpload(file);
                                    });
                                }}
                                creditLink={img.links.self}
                                creditName={img.user.name}
                                imageWidth={img.width}
                                imageHeight={img.height}
                                isBanner={isBanner}
                            />
                        ))}
                </div>
                {nextPage && (
                    <div className="wz-load-more-container">
                        <Button inverted className="wz-add-more" onClick={() => setPage(page + 1)}>
                            <Trans i18nKey="ticket.event.minisite.load-more" />
                        </Button>
                    </div>
                )}
            </div>
            <ModalEditImage
                isOpen={Boolean(imgToUplaod)}
                imageFile={imgToUplaod}
                isSubmitting={isUploading}
                onClose={() => {
                    setImgToUpload(null);
                }}
                onSubmit={handleImageUpload}
                fileTypes={fileTypes}
                maxSize={maxSize}
                {...cropModalProps}
            />
        </>
    );
};

PublicImages.propTypes = {
    isBanner: PropTypes.bool.isRequired,
    apiKeyUnsplash: PropTypes.string.isRequired
};

PublicImages.fileTypes = ['.jpg', '.jpeg', '.png', '.gif'];

export default PublicImages;
